import {
  EActionCategory,
  EDatasourceType,
  type IActionDefinitionDto,
  type IDatasourceDto
} from "@kuika/kuika-cl-model";

/**
 * Datasource qualifiers for names that more than one datasource holds (KOA-18147).
 *
 * Two datasources can each carry an action of the same name: CRUD generation and the AI's SQL tool
 * save actions without the cross-datasource name check, and TypeScript actions are never checked
 * against other datasources. Tables collide even more readily — two databases each holding a `Users`
 * table is the normal case. A surface that shows an assigned action or a table by its name alone then
 * shows two identical rows. These helpers name the datasource in front — `[CRM_DB] GetUsers` — and
 * only while the name is actually held by more than one datasource, so a unique name reads as before.
 *
 * Display only. A qualifier is never written into a symbol, an action or a label: the code generator
 * matches on those strings.
 */

/** Added when two colliding datasources would otherwise read alike. */
const INTERNAL_DATASOURCE_WORD: string = "Internal";
const EXTERNAL_DATASOURCE_WORD: string = "External";

/** The action picker's group label for a Kuika-managed database; `undefined` for every other type. */
export function getManagedDatasourceLabel(datasourceType: EDatasourceType): string | undefined {
  switch (datasourceType) {
    case EDatasourceType.ManagedDb:
      return "Managed DB (SQL Server)";
    case EDatasourceType.ManagedDbOracle:
      return "Managed DB (Oracle)";
    case EDatasourceType.ManagedDbPostgre:
      return "Managed DB (Postgre)";
    default:
      return undefined;
  }
}

/** A datasource as the action picker names its group: the managed label, else its own name. */
export function getDatasourceLabel(datasourceType: EDatasourceType, datasourceName: string | undefined): string {
  return getManagedDatasourceLabel(datasourceType) ?? datasourceName ?? "";
}

/** `[CRM_DB] GetUsers`, or the bare name without a qualifier — the one place the format is spelled. */
export function formatQualifiedName(name: string, qualifier: string | undefined): string {
  return qualifier ? `[${qualifier}] ${name}` : name;
}

interface IQualifierCandidate {
  /** The name as the user reads it. */
  name: string;
  datasourceId: string;
  datasourceLabel: string;
  isManaged: boolean;
}

/** Name key → datasource id → qualifier. Holds only the names that more than one datasource carries. */
type QualifierIndex = Map<string, Map<string, string>>;

/** Case-insensitive, like the backend's own action-name check; JS `toLowerCase` ignores the locale. */
function getNameKey(name: string): string {
  return name.toLowerCase();
}

function getQualifier(holder: IQualifierCandidate, holders: Map<string, IQualifierCandidate>): string | undefined {
  if (!holder.datasourceLabel) return undefined;

  let readsAlike: boolean = false;
  holders.forEach((other: IQualifierCandidate, datasourceId: string): void => {
    if (datasourceId !== holder.datasourceId && other.datasourceLabel === holder.datasourceLabel) {
      readsAlike = true;
    }
  });
  if (!readsAlike) return holder.datasourceLabel;

  const sourceWord: string = holder.isManaged ? INTERNAL_DATASOURCE_WORD : EXTERNAL_DATASOURCE_WORD;
  return `${holder.datasourceLabel} - ${sourceWord}`;
}

function buildQualifierIndex(candidates: readonly IQualifierCandidate[]): QualifierIndex {
  const holdersByName: Map<string, Map<string, IQualifierCandidate>> = new Map();
  candidates.forEach((candidate: IQualifierCandidate): void => {
    const nameKey: string = getNameKey(candidate.name);
    let holders: Map<string, IQualifierCandidate> | undefined = holdersByName.get(nameKey);
    if (!holders) {
      holders = new Map();
      holdersByName.set(nameKey, holders);
    }
    if (!holders.has(candidate.datasourceId)) holders.set(candidate.datasourceId, candidate);
  });

  const index: QualifierIndex = new Map();
  holdersByName.forEach((holders: Map<string, IQualifierCandidate>, nameKey: string): void => {
    if (holders.size < 2) return;

    const qualifiers: Map<string, string> = new Map();
    holders.forEach((holder: IQualifierCandidate, datasourceId: string): void => {
      const qualifier: string | undefined = getQualifier(holder, holders);
      if (qualifier) qualifiers.set(datasourceId, qualifier);
    });
    index.set(nameKey, qualifiers);
  });

  return index;
}

function getActionDisplayName(actionDefinition: IActionDefinitionDto): string {
  return actionDefinition.displayName || actionDefinition.name || "";
}

/**
 * Whether an action definition takes part: a user's action in a datasource. System functions share
 * nothing with them, and Save Record / Delete Record are one built-in pair per managed database.
 */
function isQualifiableActionDefinition(actionDefinition: IActionDefinitionDto): boolean {
  if (!actionDefinition.kDatasource_Id || !getActionDisplayName(actionDefinition)) return false;
  if (
    actionDefinition.actionType === EDatasourceType.SystemCSFunctions ||
    actionDefinition.actionType === EDatasourceType.SystemJSFunctions
  ) {
    return false;
  }
  return actionDefinition.actionCategory !== EActionCategory.ManagedDb;
}

/**
 * Keyed by the definitions array itself: the provider replaces `actionDefinitions` rather than
 * mutating it, so a new catalog is a new key and the stale index is collected with the old array.
 */
const actionQualifierIndexes: WeakMap<readonly IActionDefinitionDto[], QualifierIndex> = new WeakMap();

function getActionQualifierIndex(actionDefinitions: readonly IActionDefinitionDto[]): QualifierIndex {
  const cached: QualifierIndex | undefined = actionQualifierIndexes.get(actionDefinitions);
  if (cached) return cached;

  const index: QualifierIndex = buildQualifierIndex(
    actionDefinitions
      .filter(isQualifiableActionDefinition)
      .map((actionDefinition: IActionDefinitionDto): IQualifierCandidate => ({
        name: getActionDisplayName(actionDefinition),
        datasourceId: actionDefinition.kDatasource_Id,
        datasourceLabel: getDatasourceLabel(actionDefinition.actionType, actionDefinition.kDatasource_Name),
        isManaged: getManagedDatasourceLabel(actionDefinition.actionType) !== undefined
      }))
  );
  actionQualifierIndexes.set(actionDefinitions, index);
  return index;
}

/**
 * The datasource qualifier for an action definition — `CRM_DB`, or `CRM_DB - External` when another
 * colliding datasource reads alike — or `undefined` while its name is unique across datasources.
 */
export function getActionDatasourceQualifier(
  actionDefinition: IActionDefinitionDto | undefined,
  actionDefinitions: readonly IActionDefinitionDto[] | undefined
): string | undefined {
  if (!actionDefinition || !actionDefinitions || !isQualifiableActionDefinition(actionDefinition)) {
    return undefined;
  }

  return getActionQualifierIndex(actionDefinitions)
    .get(getNameKey(getActionDisplayName(actionDefinition)))
    ?.get(actionDefinition.kDatasource_Id);
}

export interface IQualifiableTable {
  key: string;
  title: string;
  dsId: string;
}

/**
 * Datasource qualifiers for a table list, keyed by table key; a table whose name no other datasource
 * holds has no entry. Two tables of one name inside the same datasource (two schemas) are not a
 * datasource ambiguity and get none either.
 */
export function getTableDatasourceQualifiers(
  tables: readonly IQualifiableTable[],
  datasources: readonly IDatasourceDto[] | undefined
): Map<string, string> {
  const datasourcesById: Map<string, IDatasourceDto> = new Map(
    (datasources ?? []).map((datasource: IDatasourceDto): [string, IDatasourceDto] => [datasource.id, datasource])
  );
  const index: QualifierIndex = buildQualifierIndex(
    tables.map((table: IQualifiableTable): IQualifierCandidate => {
      const datasource: IDatasourceDto | undefined = datasourcesById.get(table.dsId);
      return {
        name: table.title,
        datasourceId: table.dsId,
        datasourceLabel: datasource ? getDatasourceLabel(datasource.datasourceType, datasource.name) : "",
        isManaged: datasource ? getManagedDatasourceLabel(datasource.datasourceType) !== undefined : false
      };
    })
  );

  const qualifiers: Map<string, string> = new Map();
  tables.forEach((table: IQualifiableTable): void => {
    const qualifier: string | undefined = index.get(getNameKey(table.title))?.get(table.dsId);
    if (qualifier) qualifiers.set(table.key, qualifier);
  });
  return qualifiers;
}
